
import { Loader, } from '@googlemaps/js-api-loader';
import { Component, Prop, Ref, Vue, } from 'vue-property-decorator';
import SearchForm from '~/components/search-bar/search-bar.vue';
import { Completed, Errored, Initial, Loading, Result, } from '~/utils/models/result';

type ContentMapData = typeof import('./content-map-data.json');

declare var google: typeof window.google;

interface PostcodeResult {
	id: number;
	name: string;
	places: {
		id: number,
		name: string,
		position: google.maps.LatLng,
		distance: number,
	}[];
}

const PostcodeSearchParam = 'postcode';
const SearchResultsCount = 3;

function getPostcode(searchParam: string) {
	const regex = new RegExp(`[\\?&]${searchParam}=([^&#]*)`);
	const results = regex.exec(window.location.search);
	return results == null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
}

const InfoWindowZ1 = (() => {
	let InfoWindowZZ: number;
	return (opts: google.maps.InfoWindowOptions) => {
		let ce: google.maps.MapsEventListener;
		const infoWindow = new google.maps.InfoWindow(opts);

		if (!InfoWindowZZ) {
			InfoWindowZZ = Number(google.maps.Marker.MAX_ZINDEX);
		}

		google.maps.event.addListener(infoWindow, 'content_changed', () => {
			const content = infoWindow.getContent();
			if (typeof content === 'string') {
				const n = document.createElement('div');
				n.innerHTML = content;
				infoWindow.setContent(n);
				return;
			}

			google.maps.event.addListener(infoWindow, 'domready', () => {
				infoWindow.setZIndex(++InfoWindowZZ);
				if (ce) {
					google.maps.event.removeListener(ce);
				}

				ce = google.maps.event.addDomListener(
					((infoWindow.getContent() as Element).parentNode as Node).parentNode as Node,
					'mouseover',
					() => infoWindow.setZIndex(++InfoWindowZZ),
				);
			});
		});

		return infoWindow;
	};
})();

@Component({
	components: {
		SearchForm,
	},
})
export default class ContentMap extends Vue {

	@Prop({
		type: String,
		required: true,
	})
	googleMapsApiKey!: string;

	@Ref('content-map__map')
	mapElement!: HTMLDivElement;

	postcodeResults: Result<readonly PostcodeResult[]> = Initial();

	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	private map!: google.maps.Map;
	private geocoder!: google.maps.Geocoder;
	normalZoom = 8.5;
	// zoomIn = 9;
	normalLatitude = 50.842191;
	normalLongitude = -0.3303004;

	activeLayers: number[] = [];
	readonly searchParameter = PostcodeSearchParam;
	searchedPostcode = getPostcode(PostcodeSearchParam);
	private postcodeMarker: google.maps.Marker | null = null;

	private readonly markers = new Map<number, google.maps.Marker>();
	private readonly layers = new Map<number, Map<number, google.maps.Marker>>();
	private readonly infoWindows = new Map<number, google.maps.InfoWindow>();
	private readonly icons = new Map<number, string>();

	get ContentData() {
		return this.$store.state.contentMapData as ContentMapData;
	}

	mounted(): void {
		this.ContentData.Types.forEach((objectType) => {
			this.icons.set(objectType.Id, objectType.IconUrl);
		});
		this.activeLayers = this.ContentData.Map.Layers.map(({ Id, }) => Id);

		this.mapElement.style.width = `100%`;
		this.mapElement.style.height = `${this.ContentData.Map.Width}px`;

		const loader = new Loader({
			id: 'contentmap',
			apiKey: this.googleMapsApiKey,
			libraries: [ 'geometry', ],
		});

		loader.load()
			.then(() => this.Initialize())
			.catch((e) => console.error(e));
	}

	onPostcodeSearch(e: Event) {
		if (!(e.target instanceof HTMLFormElement)) {
			return;
		}

		const target = e.target;
		const data = new FormData(target);
		const postcode = data.get(this.searchParameter);
		if (typeof postcode !== 'string') {
			return;
		}

		e.preventDefault();

		const newurl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?${this.searchParameter}=${postcode}`;
		window.history.pushState({ path: newurl, }, '', newurl);
		this.performSearch(postcode);
	}

	toggleLayer(layerId: number) {
		if (this.activeLayers.includes(layerId)) {
			this.activeLayers = this.activeLayers.filter((v) => v !== layerId);
		} else {
			this.activeLayers.push(layerId);
		}

		this.layers.forEach((layer, layerItem) => {
			layer.forEach((marker, item) => {
				const infoWindow = this.infoWindows.get(item);
				const shouldBeVisible = this.activeLayers.includes(layerItem);
				marker.setVisible(shouldBeVisible);
				if (infoWindow && !shouldBeVisible) {
					infoWindow.close();
				}
			});
		});
	}

	private Initialize() {
		this.geocoder = new google.maps.Geocoder();
		this.map = new google.maps.Map(this.mapElement, {
			center: new google.maps.LatLng(this.normalLatitude, this.normalLongitude),
			zoom: this.normalZoom,
			disableDefaultUI: false,
			restriction: {
				latLngBounds: {
					north: 52.1,
					south: 49.82,
					east: 2.5,
					west: -3.2,
				},
			},
		});

		this.ContentData.Map.Layers.forEach((layer) => {
			const layerMarkers = new Map<number, google.maps.Marker>();
			this.layers.set(layer.Id, layerMarkers);
			layer.Places.forEach((place) => {
				place.Locations.forEach((location) => {
					const marker = this.addMarker(location);
					layerMarkers.set(location.Id, marker);
				});
			});
		});

		if (this.searchedPostcode) {
			this.performSearch(this.searchedPostcode);
		}
	}

	private addMarker(data: { Id: number, Name: string, Latitude: number, Longitude: number, TypeId: number, Contents: { Body: string, }[], }) {
		const marker = new google.maps.Marker({
			position: new google.maps.LatLng(data.Latitude, data.Longitude),
			icon: this.icons.get(data.TypeId),
			map: this.map,
			zIndex: 1,
		});

		const content = `<div>
			<h3 class="text-primary">${data.Name}</h3>
			<div>
			${data.Contents[0].Body}
			</div>
		</div>`;

		const infowindow = InfoWindowZ1({
			content,
		});

		marker.addListener('click', () => {
			this.openMarkerCenterMapOnMarker(data.Id);
		});

		this.markers.set(data.Id, marker);
		this.infoWindows.set(data.Id, infowindow);
		return marker;
	}

	openMarkerCenterMapOnMarker(id: number) {
		this.closeAllInfoWindows();

		const marker = this.markers.get(id);
		const infoWindow = this.infoWindows.get(id);
		if (!marker || !infoWindow) {
			return;
		}

		infoWindow.open(this.map, marker);

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		this.map.panTo(marker.getPosition() as any); // setCenter takes a LatLng object
	}

	private closeAllInfoWindows() {
		this.infoWindows.forEach((infoWindow) => infoWindow.close());
	}

	private performSearch(postcode: string) {
		this.postcodeResults = Loading();
		void this.geocoder.geocode({ address: postcode, }, (response, status) => {
			if (status !== google.maps.GeocoderStatus.OK || response === null) {
				this.postcodeResults = Errored();
				return;
			}

			const searchLocation = response[0].geometry.location;
			this.postcodeMarker ?? (this.postcodeMarker = new google.maps.Marker({
				map: this.map,
				icon: 'https://www.southernwater.co.uk/maps/homepin/0823b141-3201-4f41-8cec-e91a131c6fe6',
				zIndex: 2,
			}));
			this.postcodeMarker.setPosition(searchLocation);
			this.map.panTo(searchLocation);
			this.map.setZoom(12);
			this.map.setCenter(searchLocation);

			const { results, bounds, } = this.getClosestPlaces(SearchResultsCount, searchLocation);

			this.map.fitBounds(bounds);
			this.postcodeResults = Completed(results);
		});
	}

	getClosestPlaces(count: number, center: google.maps.LatLng) {
		const bounds = new google.maps.LatLngBounds();
		// bounds.extend(this.marker.getPosition());

		const results = this.ContentData.Map.Layers.map((layer) => {
			const places = layer.Places
				.flatMap(({ Locations, }) => Locations)
				.map((location) => {
					const position = new google.maps.LatLng({ lat: location.Latitude, lng: location.Longitude, });
					const distance = google.maps.geometry.spherical.computeDistanceBetween(position, center);
					return {
						id: location.Id,
						name: location.Name,
						position,
						distance,
					};
				}).sort((a, b) => a.distance - b.distance);

			const layerResults = places.slice(0, count);
			layerResults.forEach(({ position, }) => {
				bounds.extend(position);
			});

			return {
				id: layer.Id,
				name: layer.Name,
				places: layerResults,
			};
		});

		return { bounds, results, };
	}

}
